import { useEffect, useRef } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import {
  transformGeoJsonToLatLon,
  type LocalGeoJsonFeatureCollection,
} from 'utils/maplibre';
import { utmToLatLon } from 'utils/utmToLatLon';
import {
  ALMALY_FALLBACK_LOCAL_ORIGIN_EASTING,
  ALMALY_FALLBACK_LOCAL_ORIGIN_NORTHING,
  ALMALY_ROADS_COLOR,
  ALMALY_ROADS_GEOJSON_URL,
} from './roadsImporter.constants';

interface UseMineMapOverlayArgs {
  mapRef: React.RefObject<MapRef | null>;
  enabled: boolean;
  mapReady: boolean;
  showRoadsOverlay: boolean;
  proj4String?: string | null;
  localOriginEasting?: number | null;
  localOriginNorthing?: number | null;
}

const OVERLAY_SOURCE_ID = 'mine-map-roads-overlay';
const OVERLAY_LAYER_ID = 'mine-map-roads-overlay-line';

// Mine map GeoJSON overlay (car customer only) — fetched once and cached as
// transformed WGS84 GeoJSON so toggling the switch just adds/removes the
// MapLibre source/layer instead of re-fetching/re-transforming every time.
export function useMineMapOverlay({
  mapRef,
  enabled,
  mapReady,
  showRoadsOverlay,
  proj4String,
  localOriginEasting,
  localOriginNorthing,
}: UseMineMapOverlayArgs): void {
  const overlayAddedRef = useRef(false);
  const cacheRef = useRef<LocalGeoJsonFeatureCollection | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!mapReady || !showRoadsOverlay) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    let cancelled = false;

    const addOverlayLayer = async () => {
      let geojson = cacheRef.current;
      if (!geojson) {
        const response = await fetch(ALMALY_ROADS_GEOJSON_URL);
        if (!response.ok) return;
        const raw = (await response.json()) as LocalGeoJsonFeatureCollection;
        // The customer record has no localOriginEasting/localOriginNorthing
        // of its own for this site, so almaly_roads.geojson's raw local-grid
        // coordinates fall back to a derived origin — see the constant's
        // doc comment. A real customer-record value always wins.
        const effectiveLocalOriginEasting =
          localOriginEasting ?? ALMALY_FALLBACK_LOCAL_ORIGIN_EASTING;
        const effectiveLocalOriginNorthing =
          localOriginNorthing ?? ALMALY_FALLBACK_LOCAL_ORIGIN_NORTHING;
        geojson = transformGeoJsonToLatLon(raw, (x, y) => {
          const latLon = utmToLatLon(
            x,
            y,
            proj4String,
            effectiveLocalOriginEasting,
            effectiveLocalOriginNorthing
          );
          return latLon
            ? { lat: latLon.latitude, lon: latLon.longitude }
            : null;
        });
        // Only cache once proj4String is actually available — without it,
        // utmToLatLon falls back to raw-UTM parsing instead of returning
        // null, so geojson.features.length alone can't tell a real
        // transform apart from a wrong one computed before the customer's
        // projection loaded.
        if (proj4String && geojson.features.length > 0) {
          cacheRef.current = geojson;
        }
      }
      if (cancelled || !map.isStyleLoaded()) return;
      if (map.getLayer(OVERLAY_LAYER_ID)) map.removeLayer(OVERLAY_LAYER_ID);
      if (map.getSource(OVERLAY_SOURCE_ID)) map.removeSource(OVERLAY_SOURCE_ID);
      map.addSource(OVERLAY_SOURCE_ID, {
        type: 'geojson',
        data: geojson as any,
      });
      map.addLayer({
        id: OVERLAY_LAYER_ID,
        type: 'line',
        source: OVERLAY_SOURCE_ID,
        paint: {
          'line-color': ALMALY_ROADS_COLOR,
          'line-width': 2,
        },
      });
      overlayAddedRef.current = true;
    };

    void addOverlayLayer();

    return () => {
      cancelled = true;
      if (!overlayAddedRef.current) return;
      overlayAddedRef.current = false;
      try {
        if (map.getLayer(OVERLAY_LAYER_ID)) map.removeLayer(OVERLAY_LAYER_ID);
        if (map.getSource(OVERLAY_SOURCE_ID))
          map.removeSource(OVERLAY_SOURCE_ID);
      } catch {
        // map already torn down
      }
    };
  }, [
    enabled,
    mapReady,
    showRoadsOverlay,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
    mapRef,
  ]);
}
